import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { IconTag, IconPlus, IconTrash } from "@tabler/icons-react";
import { Product, Promo } from "../../../types/types";
import {
  useMerchantAllListData,
  useMerchantListData,
  useAllFMMerchantData,
} from "../../../apis/merchantApis";
import { useProductAllListData, useProductListDataById } from "../../../apis/promoApis";
import { useFetchProductItemData } from "../../../apis/productApis";

export interface PromoForm {
  merchant: string;
  merchantId?: string;
  sku: string;
  selectedProducts: Array<{
    productSku: string;
    qty: number;
  }>;
  startDate: string;
  endDate: string;
}

interface PromosProps {
  products: Product[];
  promos: Promo[];
  isLoading?: boolean;
  isSubmitting?: boolean;
  clients?: any[];
  promoForm: PromoForm;
  onPromoFormChange: (updates: Partial<PromoForm>) => void;
  onAddPromo: (e: React.FormEvent) => void;
  onDeletePromo: (id: string) => void;
  onCancel: () => void;
}

export const Promos: React.FC<PromosProps> = ({
  products,
  promos,
  isLoading,
  isSubmitting,
  clients,
  promoForm,
  onPromoFormChange,
  onAddPromo,
  onDeletePromo,
  onCancel,
}) => {
  // Fetch Redux state
  const reduxClients = useSelector((state: any) => state?.client?.data);

  // Fetch merchant data directly from API endpoints as dynamic fallback
  const { data: apiClientsResponse } = useMerchantAllListData();
  const { data: listClientsResponse } = useMerchantListData(1, 200, "", "MERCHANT");
  const { data: fmClientsResponse } = useAllFMMerchantData();

  const selectedMerchantId = promoForm.merchantId;
  const { data: merchantProductsResponse } = useProductListDataById(
    selectedMerchantId,
    true
  );

  // Helper to extract client list recursively from any payload structure
  const extractClients = (sourceData: any): any[] => {
    if (!sourceData) return [];
    if (Array.isArray(sourceData)) return sourceData;
    if (Array.isArray(sourceData?.data)) return sourceData.data;
    if (Array.isArray(sourceData?.clients)) return sourceData.clients;
    if (Array.isArray(sourceData?.data?.clients)) return sourceData.data.clients;
    if (Array.isArray(sourceData?.lmClients)) return sourceData.lmClients;
    if (Array.isArray(sourceData?.data?.lmClients)) return sourceData.data.lmClients;
    if (Array.isArray(sourceData?.merchants)) return sourceData.merchants;
    if (Array.isArray(sourceData?.data?.merchants)) return sourceData.data.merchants;
    if (Array.isArray(sourceData?.items)) return sourceData.items;
    if (Array.isArray(sourceData?.data?.items)) return sourceData.data.items;

    if (typeof sourceData === "object") {
      for (const key of Object.keys(sourceData)) {
        if (Array.isArray(sourceData[key])) {
          return sourceData[key];
        }
      }
    }
    return [];
  };

  // Combine ALL merchants across API sources into a unified list filtering for FM and BOTH access
  const allMerchants = useMemo(() => {
    const rawProps = extractClients(clients);
    const rawRedux = extractClients(reduxClients);
    const rawAll = extractClients(apiClientsResponse);
    const rawList = extractClients(listClientsResponse);
    const rawFm = extractClients(fmClientsResponse);

    const merged = [...rawFm, ...rawAll, ...rawList, ...rawProps, ...rawRedux];
    const map = new Map<string, any>();

    merged.forEach((c: any) => {
      if (!c) return;

      const rawAccess = c?.access || c?.lmClient?.access;

      // Only show merchants with access FM and BOTH
      if (rawAccess !== "FM" && rawAccess !== "BOTH") {
        return;
      }

      const name = (
        c.companyName ||
        c.company_name ||
        c.name ||
        c.contactPerson ||
        c.contact_person ||
        c.merchantName ||
        c.clientName ||
        c.title ||
        c.email ||
        c.id ||
        ""
      )
        .toString()
        .trim();

      const idVal = String(c.id || c.clientId || name);

      if (name && !map.has(idVal)) {
        map.set(idVal, {
          ...c,
          displayName: name,
          idValue: idVal,
        });
      }
    });

    return Array.from(map.values());
  }, [clients, reduxClients, apiClientsResponse, listClientsResponse, fmClientsResponse]);

  // Extract parent & variant SKUs cleanly from any payload structure
  const extractSkus = (
    items: any[]
  ): Array<{ id: string; name: string; sku: string }> => {
    if (!Array.isArray(items)) return [];
    const list: Array<{ id: string; name: string; sku: string }> = [];

    items.forEach((p: any) => {
      const parentName = (p?.itemName || p?.name || p?.title || "Product").toString();
      const rawSku =
        p?.sku && p?.sku !== "N/A"
          ? p.sku
          : p?.productSku || p?.code || "";
      const finalSku = (rawSku || parentName || p?.id || "").toString();

      if (finalSku && finalSku !== "N/A") {
        list.push({
          id: String(p?.id || finalSku),
          name: parentName,
          sku: finalSku,
        });
      }

      // Unpack variant SKUs
      if (Array.isArray(p?.variants) && p.variants.length > 0) {
        p.variants.forEach((v: any) => {
          const vSku = (v?.sku || v?.optionValue || "").toString();
          const vName = v?.optionName ? `${parentName} (${v.optionName})` : parentName;
          if (vSku && vSku !== "N/A") {
            list.push({
              id: String(v?.id || vSku),
              name: vName,
              sku: vSku,
            });
          }
        });
      }
    });

    return list;
  };

  // Combine products list dynamically based on selected merchant
  const availableProducts = useMemo<any[]>(() => {
    if (!selectedMerchantId) return [];
    const extracted = extractSkus(
      Array.isArray(merchantProductsResponse)
        ? merchantProductsResponse
        : Array.isArray(merchantProductsResponse?.data)
          ? merchantProductsResponse.data
          : []
    );

    const map = new Map<string, { id: string; name: string; sku: string }>();

    extracted.forEach((item) => {
      if (item.sku && !map.has(item.sku)) {
        map.set(item.sku, item);
      }
    });

    return Array.from(map.values());
  }, [selectedMerchantId, merchantProductsResponse]);

  const isMerchantSelected = Boolean(promoForm.merchantId);

  const isAddSkuDisabled =
    !isMerchantSelected ||
    availableProducts.length === 0 ||
    promoForm.selectedProducts.length >= availableProducts.length;

  const addProductRow = () => {
    if (isAddSkuDisabled) return;
    onPromoFormChange({
      selectedProducts: [...promoForm.selectedProducts, { productSku: "", qty: 1 }],
    });
  };

  const removeProductRow = (index: number) => {
    const updated = [...promoForm.selectedProducts];
    updated.splice(index, 1);
    onPromoFormChange({ selectedProducts: updated });
  };

  const updateProductRow = (index: number, field: "productSku" | "qty", value: string | number) => {
    const updated = [...promoForm.selectedProducts];
    updated[index] = { ...updated[index], [field]: value };
    onPromoFormChange({ selectedProducts: updated });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      {/* Active Promotions List */}
      <div className="lg:col-span-7 space-y-3">
        <h3 className="text-sm font-medium text-[#212121]">Active Promotions</h3>
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((n) => (
              <div key={n} className="bg-white rounded-xl border border-slate-200 p-4 space-y-2 animate-pulse">
                <div className="h-4 bg-slate-200 rounded w-1/3" />
                <div className="h-3 bg-slate-100 rounded w-2/3" />
              </div>
            ))}
          </div>
        ) : promos.length === 0 ? (
          <p className="text-sm text-[#212121]/50 italic">No promotions available.</p>
        ) : (
          promos.map((p) => (
            <div key={p.id} className="bg-white rounded-xl border border-slate-200 p-3.5 shadow-sm space-y-2">
              <div className="flex justify-between items-start">
                <div>
                  <span className="font-mono text-sm font-bold text-yellow-700">{p.sku}</span>
                  <span className="text-xs text-[#212121]/60 block">{p.merchant}</span>
                </div>
                <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${(p.status || "ACTIVE") === "ACTIVE"
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    : "bg-rose-50 text-rose-700 border border-rose-200"
                  }`}>
                  {p.status || "ACTIVE"}
                </span>
              </div>
              <div className="text-xs text-[#212121]/80 space-y-1 bg-slate-50 p-2 rounded-lg border border-slate-100">
                {p.products.map((item, idx) => (
                  <div key={idx} className="flex justify-between font-medium">
                    <span>{item.productSku}</span>
                    <span>Qty: {item.qty}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between items-center pt-1 text-xs text-[#212121]/60">
                <span>
                  {p.startDate} → {p.endDate}
                </span>
                <button
                  type="button"
                  onClick={() => onDeletePromo(p.id)}
                  className="text-rose-600 hover:text-rose-700 font-medium hover:underline text-xs"
                >
                  Deactivate
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Promo Campaign Creator Form */}
      <form noValidate onSubmit={onAddPromo} className="lg:col-span-5 bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-5">
        <div className="border-b border-slate-100 pb-3">
          <h2 className="text-sm font-medium text-[#212121] flex items-center gap-2">
            <IconTag className="w-4 h-4 text-yellow-600" /> Promo Campaign Creator
          </h2>
          <p className="text-xs text-[#212121]/60">Define a promotional bundle with multiple SKUs.</p>
        </div>

        {/* Merchant & Promo Code */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-[#212121]/70 mb-1">Merchant</label>
            <select
              value={promoForm.merchantId || ""}
              onChange={(e) => {
                const val = e.target.value;
                const selected = allMerchants.find(
                  (c: any) =>
                    c.idValue === val ||
                    c.id === val ||
                    c.displayName === val
                );
                onPromoFormChange({
                  merchantId: val ? (selected?.idValue || selected?.id || val) : "",
                  merchant: val ? (selected?.displayName || selected?.companyName || selected?.name || val) : "",
                  selectedProducts: [{ productSku: "", qty: 1 }],
                });
              }}
              className="w-full px-3.5 py-2.5 bg-[#FFFDF5] border border-amber-200/80 rounded-2xl text-sm text-[#212121] focus:outline-none focus:ring-2 focus:ring-amber-400/50 shadow-xs"
            >
              <option value="">Select Merchant</option>
              {allMerchants.map((c: any) => (
                <option key={c.idValue} value={c.idValue}>
                  {c.displayName}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#212121]/70 mb-1">Promo SKU / Code *</label>
            <input
              placeholder="e.g. PROMO-SUMMER26"
              value={promoForm.sku}
              onChange={(e) => onPromoFormChange({ sku: e.target.value })}
              className="w-full px-3.5 py-2.5 bg-[#FFFDF5] border border-amber-200/80 rounded-2xl text-sm text-[#212121] focus:outline-none focus:ring-2 focus:ring-amber-400/50 shadow-xs"
            />
          </div>
        </div>

        {/* Bundle Items */}
        <div className="space-y-2.5">
          <div className="flex justify-between items-center">
            <span className="text-xs font-medium text-[#212121]/70">Bundle Items</span>
            <button
              type="button"
              disabled={isAddSkuDisabled}
              onClick={addProductRow}
              className={`text-xs font-semibold text-[#B47814] hover:text-amber-800 flex items-center gap-1 transition ${isAddSkuDisabled
                  ? "opacity-50 cursor-not-allowed"
                  : "cursor-pointer"
                }`}
            >
              <IconPlus size={14} /> Add SKU
            </button>
          </div>
          <div className="space-y-2">
            {promoForm.selectedProducts.map((item, idx) => {
              const selectedOtherSkus = promoForm.selectedProducts
                .map((p, i) => (i !== idx ? p.productSku : null))
                .filter(Boolean);

              const rowAvailableProducts = availableProducts.filter(
                (p: any) => p.sku === item.productSku || !selectedOtherSkus.includes(p.sku)
              );

              return (
                <div key={idx} className="flex items-center gap-2.5">
                  <select
                    disabled={!isMerchantSelected}
                    value={item.productSku}
                    onChange={(e) => updateProductRow(idx, "productSku", e.target.value)}
                    className={`flex-1 px-4 py-2.5 bg-[#FFFDF5] border border-amber-200/80 rounded-2xl text-sm text-[#212121] focus:outline-none focus:ring-2 focus:ring-amber-400/50 shadow-xs ${!isMerchantSelected ? "opacity-60 cursor-not-allowed bg-gray-100" : ""
                      }`}
                  >
                    <option value="">
                      {!isMerchantSelected ? "Select Merchant First" : "Select Product SKU"}
                    </option>
                    {rowAvailableProducts.map((p: any) => (
                      <option key={p.id || p.sku} value={p.sku}>
                        {p.sku} {p.name && p.name !== p.sku ? `— ${p.name}` : ""}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    min="1"
                    value={item.qty}
                    onChange={(e) => updateProductRow(idx, "qty", Number(e.target.value))}
                    className="w-20 px-3 py-2.5 bg-[#FFFDF5] border border-amber-200/80 rounded-2xl text-sm text-center font-medium text-[#212121] focus:outline-none focus:ring-2 focus:ring-amber-400/50 shadow-xs"
                  />
                  {promoForm.selectedProducts.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeProductRow(idx)}
                      className="text-slate-400 hover:text-rose-500 transition p-1"
                    >
                      <IconTrash size={16} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Start Date & End Date */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-[#212121]/70 mb-1">Start Date *</label>
            <input
              type="date"
              value={promoForm.startDate}
              onChange={(e) => onPromoFormChange({ startDate: e.target.value })}
              className="w-full px-3.5 py-2.5 bg-[#FFFDF5] border border-amber-200/80 rounded-2xl text-sm text-[#212121] focus:outline-none focus:ring-2 focus:ring-amber-400/50 shadow-xs"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#212121]/70 mb-1">End Date *</label>
            <input
              type="date"
              value={promoForm.endDate}
              onChange={(e) => onPromoFormChange({ endDate: e.target.value })}
              className="w-full px-3.5 py-2.5 bg-[#FFFDF5] border border-amber-200/80 rounded-2xl text-sm text-[#212121] focus:outline-none focus:ring-2 focus:ring-amber-400/50 shadow-xs"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-2 border-t border-slate-100">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm text-[#212121]/70 border border-slate-200 rounded-xl hover:bg-slate-50 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className={`px-5 py-2 bg-yellow-100 text-yellow-700 rounded-xl text-sm font-medium hover:bg-yellow-200 transition shadow-sm ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""
              }`}
          >
            {isSubmitting ? "Adding..." : "Add Promo"}
          </button>
        </div>
      </form>
    </div>
  );
};